import { handleError } from "../../../helperFunctions";
import { BaseAPIResponse } from "../../../utils/types";
import { baseApi } from "../../api";
import {
  GetPaymentLinkQuery,
  GetPaymentLinkResponse,
  VerifyPayment,
} from "../payment/payment.type";
import {
  BulkUploadContributionsResponse,
  ContributionDirectDebitPayload,
  CooperativeContributionsDetails,
  CooperativeContributionsHistory,
  CooperativeContributionsHistoryQuery,
  MemberContributionsDetails,
  MemberContributionsDetailsQuery,
  NewAdminBulkUploadContribution,
} from "./contributions.type";

export const contributionsApi = baseApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getMemberContributionsDetails: builder.query<
      MemberContributionsDetails,
      MemberContributionsDetailsQuery
    >({
      query: (params) => {
        return {
          url: `/Saving/My/Cooperative`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getCooperativeContributionsDetails: builder.query<
      CooperativeContributionsDetails,
      MemberContributionsDetailsQuery
    >({
      query: (params) => {
        return {
          url: `/Saving/Cooperative/Admin`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    getContributionsPaystackPayment: builder.mutation<
      GetPaymentLinkResponse,
      GetPaymentLinkQuery
    >({
      query: (params) => {
        return {
          url: `/Payment/Get/link`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    verifyContributionsPayment: builder.mutation<
      BaseAPIResponse,
      VerifyPayment
    >({
      query: (payload) => {
        return {
          url: `/Saving/My/Cooperative`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    uploadContribution: builder.mutation<BaseAPIResponse, FormData>({
      query: (payload) => {
        return {
          url: `/Saving/Member/Manual`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    adminUploadContribution: builder.mutation<BaseAPIResponse, FormData>({
      query: (payload) => {
        return {
          url: `/Saving/Member/Manual/Admin`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    downloadBulkContributionsTemplate: builder.mutation<
      void,
      { fileName: string; coop: number; file: "csv"; unique: string }
    >({
      query: ({ fileName, ...params }) => {
        return {
          url: "Saving/Member/Manual/file/upload",
          method: "GET",
          params: params,
          responseHandler: async (response) => {
            const blob = await response.blob();

            const filename = fileName;

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            return undefined;
          },
          responseType: "blob",
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    adminBulkUploadContribution: builder.mutation<
      BulkUploadContributionsResponse,
      FormData
    >({
      query: (payload) => {
        return {
          url: `/Saving/Member/Manual/file/upload`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    newAdminBulkUploadContribution: builder.mutation<
      BaseAPIResponse,
      NewAdminBulkUploadContribution
    >({
      query: (payload) => {
        return {
          url: `/Saving/Member/Manual/file/v2/upload`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),

    getCooperativeContributionsHistory: builder.query<
      CooperativeContributionsHistory,
      CooperativeContributionsHistoryQuery
    >({
      query: (params) => {
        return {
          url: `/Saving/Cooperative/Admin/History`,
          method: "GET",
          params: params,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, false);
        }
      },
    }),

    contributionDirectDebit: builder.mutation<
      BaseAPIResponse,
      ContributionDirectDebitPayload
    >({
      query: (payload) => {
        return {
          url: `/Collection/DirectDebit/Member/Manual/file/upload`,
          method: "POST",
          body: payload,
        };
      },
      async onQueryStarted(arg, { queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          handleError(err, true);
        }
      },
    }),
  }),
});

export const {
  useGetMemberContributionsDetailsQuery,
  useGetCooperativeContributionsDetailsQuery,
  useGetContributionsPaystackPaymentMutation,
  useVerifyContributionsPaymentMutation,
  useUploadContributionMutation,
  useDownloadBulkContributionsTemplateMutation,
  useAdminBulkUploadContributionMutation,
  useNewAdminBulkUploadContributionMutation,
  useGetCooperativeContributionsHistoryQuery,
  useAdminUploadContributionMutation,
  useContributionDirectDebitMutation,
} = contributionsApi;
